import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wallet, FileText, DollarSign, ClipboardCheck } from "lucide-react";

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export default function CleanerEarnings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/my/payouts").then((r) => setData(r.data));
  }, []);

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/payouts/${user.id}/statement.pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my_statement.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error("Could not download statement");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 pb-24">
      <h1 className="font-display text-2xl font-semibold tracking-tight mb-1 mt-2">My Earnings</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Payouts from your completed jobs. Each property's cost is split evenly between assigned cleaners.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <Card className="p-5 bg-primary text-primary-foreground">
          <DollarSign className="h-5 w-5 mb-2 opacity-90" strokeWidth={1.7} />
          <div className="font-display text-3xl font-semibold">
            ${data?.total?.toFixed(2) ?? "0.00"}
          </div>
          <p className="text-sm opacity-80 mt-1">Total earned</p>
        </Card>
        <Card className="p-5">
          <ClipboardCheck className="h-5 w-5 text-emerald-600 mb-2" strokeWidth={1.7} />
          <div className="font-display text-3xl font-semibold">{data?.job_count ?? 0}</div>
          <p className="text-sm text-muted-foreground mt-1">Completed jobs</p>
        </Card>
      </div>

      <Button
        className="w-full h-11 mb-6"
        onClick={downloadStatement}
        disabled={downloading || !data?.job_count}
        data-testid="my-statement-button"
      >
        <FileText className="h-4 w-4 mr-2" />
        Download my statement (PDF)
      </Button>

      <Card className="p-4">
        <h2 className="font-display font-semibold flex items-center gap-2 mb-3">
          <Wallet className="h-4 w-4 text-primary" /> Payout history
        </h2>
        {!data || data.line_items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No completed jobs yet. Finish a job to start earning.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {data.line_items
              .slice()
              .reverse()
              .map((li, i) => (
                <div key={i} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{li.property}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(li.date)}</p>
                  </div>
                  <span className="font-semibold text-primary">${li.share.toFixed(2)}</span>
                </div>
              ))}
          </div>
        )}
      </Card>
    </div>
  );
}
